/**
 * LOA Service
 *
 * Location avec Option d'Achat (Lease-to-Own) contract management.
 */

import apiClient, {
  APIResponse,
  PaginatedResponse,
  unwrapResponse,
  unwrapPaginatedResponse,
} from './apiClient';
import { ENDPOINTS, PAGINATION } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export type LOAStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'VALIDATED' | 'ACTIVE' | 'CLOSED' | 'CANCELLED' | 'DEFAULTED';
export type InstallmentStatus = 'PENDING' | 'PAID' | 'PARTIAL' | 'OVERDUE';

export interface LOAInstallment {
  id: string;
  numero_echeance: number;
  date_echeance: string;
  montant_echeance: number;
  part_capital: number;
  part_interets: number;
  capital_restant: number;
  montant_paye: number;
  date_paiement?: string;
  status: InstallmentStatus;
  retard_jours: number;
  notes?: string;
}

export interface LOAContract {
  id: string;
  code: string;
  client_id: string;
  client_nom?: string;
  machine_id: string;
  machine_nom?: string;
  machine_reference?: string;
  commercial_id: string;
  commercial_nom?: string;
  date_debut: string;
  date_fin: string;
  duree_mois: number;
  valeur_machine: number;
  apport_initial: number;
  taux_interet_annuel: number;
  montant_mensualite: number;
  valeur_residuelle: number;
  montant_interets_total: number;
  montant_total_finance: number;
  apport_paye: boolean;
  montant_paye: number;
  nombre_echeances_retard: number;
  status: LOAStatus;
  nombre_echeances_payees: number;
  option_achat_levee: boolean;
  date_levee_option?: string;
  etat_lieux_entree_id?: string;
  etat_lieux_sortie_id?: string;
  notes?: string;
  created_at: string;
  updated_at: string;

  // Computed
  progress_percent: number;
}

export interface LOAContractCreate {
  client_id: string;
  machine_id: string;
  date_debut: string;
  duree_mois: number;
  valeur_machine?: number; // Uses machine price if not specified
  apport_initial: number;
  taux_interet_annuel: number; // Annual percentage
  valeur_residuelle: number;
  notes?: string;
}

export interface LOAContractUpdate {
  notes?: string;
  conditions_particulieres?: string;
}

export interface LOAPayment {
  echeance_id: string;
  montant: number;
  date_paiement?: string;
  methode_paiement: 'ESPECES' | 'CHEQUE' | 'VIREMENT' | 'MOBILE_MONEY_MTN' | 'MOBILE_MONEY_MOOV';
  reference?: string;
  notes?: string;
}

export interface LOASimulation {
  duree_mois: number;
  valeur_machine: number;
  apport_initial: number;
  taux_interet_annuel: number;
  valeur_residuelle: number;
  montant_mensualite: number;
  montant_interets_total: number;
  montant_total_finance: number;
  echeancier: Omit<LOAInstallment, 'id' | 'montant_paye' | 'date_paiement' | 'status' | 'retard_jours' | 'notes'>[];
}

export interface LOADefaultInfo {
  contract_id: string;
  nombre_impayes_consecutifs: number;
  seuil_defaut: number;
  is_in_default: boolean;
  echeances_en_retard: LOAInstallment[];
  montant_total_impaye: number;
  jours_retard_max: number;
}

export interface LOATransfer {
  date_transfert: string;
  paiement_option_achat: number;
  methode_paiement: 'ESPECES' | 'CHEQUE' | 'VIREMENT' | 'MOBILE_MONEY_MTN' | 'MOBILE_MONEY_MOOV';
  reference_paiement?: string;
}

// =============================================================================
// List Params
// =============================================================================

export interface LOAListParams {
  page?: number;
  page_size?: number;
  client_id?: string;
  machine_id?: string;
  status?: LOAStatus;
  in_default?: boolean;
  date_from?: string;
  date_to?: string;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

// =============================================================================
// LOA Service
// =============================================================================

export const loaService = {
  // ---------------------------------------------------------------------------
  // Contracts
  // ---------------------------------------------------------------------------

  async listContracts(params: LOAListParams = {}) {
    const { page = PAGINATION.DEFAULT_PAGE, page_size = PAGINATION.DEFAULT_PAGE_SIZE, ...filters } = params;
    const response = await apiClient.get<PaginatedResponse<LOAContract>>(ENDPOINTS.LOA, {
      params: { page, page_size, ...filters },
    });
    return unwrapPaginatedResponse(response);
  },

  async getContract(id: string): Promise<LOAContract> {
    const response = await apiClient.get<APIResponse<LOAContract>>(`${ENDPOINTS.LOA}/${id}`);
    return unwrapResponse(response);
  },

  async createContract(data: LOAContractCreate): Promise<LOAContract> {
    const response = await apiClient.post<APIResponse<LOAContract>>(ENDPOINTS.LOA, data);
    return unwrapResponse(response);
  },

  async updateContract(id: string, data: LOAContractUpdate): Promise<LOAContract> {
    const response = await apiClient.patch<APIResponse<LOAContract>>(`${ENDPOINTS.LOA}/${id}`, data);
    return unwrapResponse(response);
  },

  async submitForApproval(id: string): Promise<LOAContract> {
    const response = await apiClient.post<APIResponse<LOAContract>>(`${ENDPOINTS.LOA}/${id}/soumettre`);
    return unwrapResponse(response);
  },

  async validateContract(id: string): Promise<LOAContract> {
    const response = await apiClient.post<APIResponse<LOAContract>>(`${ENDPOINTS.LOA}/${id}/valider`);
    return unwrapResponse(response);
  },

  async approveContract(id: string, siteStockId?: string): Promise<LOAContract> {
    const config = siteStockId ? { params: { site_stock_id: siteStockId } } : undefined;
    const response = await apiClient.post<APIResponse<LOAContract>>(
      `${ENDPOINTS.LOA}/${id}/approuver`,
      undefined,
      config,
    );
    return unwrapResponse(response);
  },

  async listPendingApproval() {
    return this.listContracts({ status: 'PENDING_APPROVAL' as LOAStatus });
  },

  async markApportPaid(id: string, data: { mode_paiement: string; reference?: string }): Promise<LOAContract> {
    const response = await apiClient.post<APIResponse<LOAContract>>(`${ENDPOINTS.LOA}/${id}/apport-paye`, data);
    return unwrapResponse(response);
  },

  async activateContract(id: string): Promise<LOAContract> {
    const response = await apiClient.post<APIResponse<LOAContract>>(`${ENDPOINTS.LOA}/${id}/activer`);
    return unwrapResponse(response);
  },

  async cancelContract(id: string, reason: string): Promise<LOAContract> {
    const response = await apiClient.post<APIResponse<LOAContract>>(`${ENDPOINTS.LOA}/${id}/annuler`, { reason });
    return unwrapResponse(response);
  },

  async getContractPdf(id: string): Promise<Blob> {
    const response = await apiClient.get(`${ENDPOINTS.LOA}/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // Installments (Échéancier)
  // ---------------------------------------------------------------------------

  async getInstallments(contractId: string): Promise<LOAInstallment[]> {
    const response = await apiClient.get<APIResponse<LOAInstallment[]>>(`${ENDPOINTS.LOA}/${contractId}/echeancier`);
    return unwrapResponse(response);
  },

  async getInstallment(contractId: string, installmentId: string): Promise<LOAInstallment> {
    // No individual installment endpoint - fetch all and filter
    const installments = await this.getInstallments(contractId);
    const found = installments.find(i => i.id === installmentId);
    if (!found) throw new Error('Installment not found');
    return found;
  },

  async payInstallment(data: LOAPayment): Promise<{
    echeance: LOAInstallment;
    is_fully_paid: boolean;
    reste_a_payer: number;
    contrat_nombre_echeances_payees: number;
  }> {
    const response = await apiClient.post<APIResponse<{
      echeance: LOAInstallment;
      is_fully_paid: boolean;
      reste_a_payer: number;
      contrat_nombre_echeances_payees: number;
    }>>(`${ENDPOINTS.LOA}/echeances/${data.echeance_id}/payer`, data);
    return unwrapResponse(response);
  },

  async getOverdueInstallments(): Promise<LOAInstallment[]> {
    // Use /with-overdue endpoint which returns contracts with overdue payments
    // Return empty array as fallback - overdue info is per-contract via default-info
    try {
      const response = await apiClient.get<PaginatedResponse<LOAContract>>(`${ENDPOINTS.LOA}/with-overdue`);
      const contracts = unwrapPaginatedResponse(response).data;
      // Flatten overdue installments from all contracts
      const allInstallments: LOAInstallment[] = [];
      for (const c of contracts) {
        try {
          const installments = await this.getInstallments(c.id);
          allInstallments.push(...installments.filter(i => i.status === 'OVERDUE'));
        } catch { /* skip */ }
      }
      return allInstallments;
    } catch {
      return [];
    }
  },

  // ---------------------------------------------------------------------------
  // Default Management
  // ---------------------------------------------------------------------------

  async getDefaultInfo(contractId: string): Promise<LOADefaultInfo> {
    const response = await apiClient.get<APIResponse<LOADefaultInfo>>(`${ENDPOINTS.LOA}/${contractId}/default-info`);
    return unwrapResponse(response);
  },

  async processReminders(): Promise<{ processed: number; reminders_sent: number; defaults_triggered: number }> {
    // No dedicated process-reminders endpoint
    return { processed: 0, reminders_sent: 0, defaults_triggered: 0 };
  },

  async getContractsInDefault(): Promise<LOAContract[]> {
    // Use list with DEFAULTED status filter
    try {
      const result = await this.listContracts({ status: 'DEFAULTED' as LOAStatus });
      return result.data;
    } catch {
      return [];
    }
  },

  // ---------------------------------------------------------------------------
  // Ownership Transfer
  // ---------------------------------------------------------------------------

  async transferOwnership(contractId: string, data: LOATransfer): Promise<{
    transfer_completed: boolean;
    contrat: LOAContract;
    message: string;
  }> {
    const response = await apiClient.post<APIResponse<{
      transfer_completed: boolean;
      contrat: LOAContract;
      message: string;
    }>>(`${ENDPOINTS.LOA}/${contractId}/transferer`, data);
    return unwrapResponse(response);
  },

  // ---------------------------------------------------------------------------
  // Simulation
  // ---------------------------------------------------------------------------

  async simulate(data: Omit<LOAContractCreate, 'client_id' | 'machine_id' | 'date_debut' | 'notes'>): Promise<LOASimulation> {
    // No dedicated simulate endpoint - compute client-side
    const { duree_mois, valeur_machine = 0, apport_initial, taux_interet_annuel, valeur_residuelle } = data;
    const montant_finance = valeur_machine - apport_initial;
    const taux_mensuel = taux_interet_annuel / 100 / 12;
    let mensualite: number;
    if (taux_mensuel === 0) {
      mensualite = montant_finance / duree_mois;
    } else {
      mensualite = montant_finance * (taux_mensuel * Math.pow(1 + taux_mensuel, duree_mois)) / (Math.pow(1 + taux_mensuel, duree_mois) - 1);
    }
    const montant_interets_total = mensualite * duree_mois - montant_finance;
    return {
      duree_mois,
      valeur_machine,
      apport_initial,
      taux_interet_annuel,
      valeur_residuelle,
      montant_mensualite: Math.round(mensualite),
      montant_interets_total: Math.round(montant_interets_total),
      montant_total_finance: Math.round(montant_finance + montant_interets_total + valeur_residuelle),
      echeancier: [],
    };
  },

  // ---------------------------------------------------------------------------
  // Statistics
  // ---------------------------------------------------------------------------

  async getStatistics(): Promise<{
    total_contracts: number;
    active_contracts: number;
    contracts_in_default: number;
    total_portfolio_value: number;
    total_collected: number;
    total_remaining: number;
    average_interest_rate: number;
  }> {
    // No dedicated statistics endpoint - return defaults
    return {
      total_contracts: 0,
      active_contracts: 0,
      contracts_in_default: 0,
      total_portfolio_value: 0,
      total_collected: 0,
      total_remaining: 0,
      average_interest_rate: 0,
    };
  },
};

export default loaService;
